import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Card, Table, Tag, Space, Button, Tooltip, Typography, Popconfirm, Modal, Select, Switch, InputNumber, Drawer, Input,
  Statistic, Row, Col, Badge, Alert, message,
} from 'antd'
import {
  ReloadOutlined, BuildOutlined, LoadingOutlined, RollbackOutlined, PlusOutlined, DeleteOutlined, PushpinOutlined, EditOutlined,
} from '@ant-design/icons'
import crsService, { errMsg } from '../../services/crsService'

const { Text } = Typography

const formatSize = (b) => {
  if (!b) return '—'
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}

const formatAge = (s) => {
  if (s === null || s === undefined) return '—'
  if (s < 60) return `${s} 秒前`
  if (s < 3600) return `${Math.floor(s / 60)} 分钟前`
  if (s < 86400) return `${Math.floor(s / 3600)} 小时前`
  return `${Math.floor(s / 86400)} 天前`
}

const MATCH = {
  match: { color: 'green', label: '一致' },
  mismatch: { color: 'orange', label: '不一致' },
  unknown: { color: 'default', label: '未上报' },
  none: { color: 'default', label: '—' },
}

const BUILD_STATUS = {
  queued: { color: 'default', label: '排队' },
  running: { color: 'processing', label: '构建中' },
  success: { color: 'green', label: '成功' },
  failed: { color: 'red', label: '失败' },
}

// ---------------- 构建弹窗(选上游 tag → 轮询构建日志) ----------------
const BuildModal = ({ open, onClose, upstream, activeBuild, onDone }) => {
  const [tag, setTag] = useState()
  const [build, setBuild] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const logRef = useRef(null)

  // 只在打开时取一次进行中的构建,之后由轮询更新(父组件刷新不覆盖日志)
  useEffect(() => {
    if (open) setBuild(activeBuild || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  useEffect(() => {
    if (open && !tag && upstream?.newest) setTag(upstream.newest)
  }, [open, tag, upstream])

  const running = build && (build.status === 'queued' || build.status === 'running')

  useEffect(() => {
    if (!open || !build?.id) return undefined
    let stop = false
    const tick = async () => {
      try {
        const b = await crsService.getBuild(build.id)
        if (stop) return
        setBuild(b)
        if (b.status === 'success' || b.status === 'failed') {
          onDone()
          return
        }
      } catch (e) {
        // 网络抖动:继续轮询
      }
      if (!stop) setTimeout(tick, 2000)
    }
    const t = setTimeout(tick, build.log_tail === undefined ? 0 : 2000)
    return () => {
      stop = true
      clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, build?.id])

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [build?.log_tail])

  const submit = async () => {
    setSubmitting(true)
    try {
      setBuild(await crsService.build(tag))
    } catch (e) {
      message.error(errMsg(e, '构建失败'))
    } finally {
      setSubmitting(false)
    }
  }

  const options = (upstream?.tags || []).map((t) => ({ value: t.tag, label: t.tag }))

  return (
    <Modal
      title="构建新版本"
      open={open}
      onCancel={onClose}
      width={760}
      destroyOnClose
      footer={
        build
          ? [
            !running && <Button key="new" onClick={() => setBuild(null)}>新构建</Button>,
            <Button key="close" type="primary" onClick={onClose}>{running ? '后台继续' : '关闭'}</Button>,
          ].filter(Boolean)
          : [
            <Button key="cancel" onClick={onClose}>取消</Button>,
            <Button key="ok" type="primary" loading={submitting} disabled={!tag} onClick={submit}>构建</Button>,
          ]
      }
    >
      {!build ? (
        <Space>
          <span>上游版本</span>
          <Select
            showSearch
            style={{ width: 220 }}
            value={tag}
            onChange={setTag}
            options={options}
            placeholder="vX.Y.Z"
            notFoundContent={upstream?.error ? '无法获取上游列表' : null}
          />
          <Tooltip title="构建含回归闸门,通过后登记为新版本,不会自动发布">
            <Text type="secondary">?</Text>
          </Tooltip>
        </Space>
      ) : (
        <>
          <Space style={{ marginBottom: 8 }}>
            <Text strong>{build.version}</Text>
            <Tag color={BUILD_STATUS[build.status]?.color}>{BUILD_STATUS[build.status]?.label || build.status}</Tag>
            {build.gate_summary && <Text type="secondary" style={{ fontSize: 12 }}>{build.gate_summary}</Text>}
          </Space>
          <pre
            ref={logRef}
            style={{
              maxHeight: 360, overflow: 'auto', fontSize: 12, background: '#0b0d12', color: '#d6deeb',
              padding: 12, borderRadius: 6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}
          >
            {build.log_tail || '等待输出…'}
          </pre>
        </>
      )}
    </Modal>
  )
}

// ---------------- 金丝雀弹窗 ----------------
const CanaryModal = ({ release, nodes, onClose, onDone }) => {
  const [selected, setSelected] = useState([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (release) setSelected(nodes.filter((n) => n.pinned_version === release.version).map((n) => n.id))
  }, [release, nodes])

  const submit = async () => {
    setSubmitting(true)
    try {
      await crsService.canary(release.version, selected)
      message.success(`已钉 ${selected.length} 台到 ${release.version}`)
      onDone()
    } catch (e) {
      message.error(errMsg(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={`金丝雀 · ${release?.version || ''}`}
      open={!!release}
      onCancel={onClose}
      onOk={submit}
      okText={`钉 ${selected.length} 台`}
      okButtonProps={{ disabled: !selected.length }}
      confirmLoading={submitting}
      width={620}
      destroyOnClose
    >
      <Table
        size="small"
        rowKey="id"
        dataSource={nodes}
        pagination={false}
        scroll={{ y: 360 }}
        rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
        columns={[
          { title: '节点', dataIndex: 'name', render: (v, r) => <span>{v} <Text type="secondary" style={{ fontSize: 12 }}>{r.ip}</Text></span> },
          { title: '当前', dataIndex: 'active_version', width: 140, render: (v) => v || '—' },
          { title: '钉', dataIndex: 'pinned_version', width: 120, render: (v) => (v ? <Tag icon={<PushpinOutlined />}>{v}</Tag> : null) },
        ]}
      />
    </Modal>
  )
}

// ---------------- 排除规则抽屉 ----------------
const ExclusionsDrawer = ({ domain, onClose, onSave }) => {
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (domain) setText((domain.exclusions || []).join('\n'))
  }, [domain])
  const lines = text.split('\n').filter((l) => l.trim()).length
  const save = async () => {
    setSaving(true)
    const ok = await onSave(domain, { crs_exclusions: text.split('\n').map((l) => l.trim()).filter(Boolean) })
    setSaving(false)
    if (ok) onClose()
  }
  return (
    <Drawer
      title={`排除规则 · ${domain?.domain || ''}`}
      open={!!domain}
      onClose={onClose}
      width={640}
      extra={<Button type="primary" loading={saving} onClick={save}>保存</Button>}
    >
      <Input.TextArea
        value={text}
        onChange={(e) => setText(e.target.value)}
        autoSize={{ minRows: 16, maxRows: 32 }}
        style={{ fontFamily: 'monospace', fontSize: 12 }}
        placeholder="SecRuleRemoveById 942100"
      />
      <Text type={lines > 200 ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>每行一条 · {lines}/200</Text>
    </Drawer>
  )
}

// ---------------- WAF 分类开关抽屉 ----------------
const CategoriesDrawer = ({ domain, catalog, onClose, onSave }) => {
  const [busy, setBusy] = useState(null)
  const pl = domain?.paranoia_level || 1
  const state = domain?.categories || {}
  const toggle = async (key, on) => {
    setBusy(key)
    await onSave(domain, { categories: { [key]: on } })
    setBusy(null)
  }
  return (
    <Drawer title={`WAF 分类 · ${domain?.domain || ''}`} open={!!domain} onClose={onClose} width={520}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        关闭的分类下发为 SecRuleRemoveById(追加在排除规则之后);规则数按当前 PL{pl}{catalog?.count_source === 'reference' ? ' · 计数取自参考规则树(尚无规则包)' : ''}
      </Text>
      <Table
        size="small"
        rowKey="key"
        style={{ marginTop: 8 }}
        pagination={false}
        dataSource={catalog?.categories || []}
        columns={[
          {
            title: '分类',
            dataIndex: 'label',
            render: (v, r) => (
              <Space size={4}>
                <span>{v}</span>
                {r.source === 'signature' && <Tooltip title="签名规则集 bad_robots(非 CRS)"><Tag>签名</Tag></Tooltip>}
                {r.response_body && <Tooltip title="开启后边缘缓冲并检查响应前 64 KB"><Tag color="orange">响应体</Tag></Tooltip>}
              </Space>
            ),
          },
          { title: '区间', dataIndex: 'ranges', render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{(v || []).join(', ') || '—'}</Text> },
          { title: '规则', width: 60, render: (_, r) => r.rule_count_by_pl?.[pl] ?? r.rule_count_total },
          {
            title: '',
            width: 60,
            render: (_, r) => (
              <Switch size="small" checked={!!state[r.key]} loading={busy === r.key} onChange={(on) => toggle(r.key, on)} />
            ),
          },
        ]}
      />
    </Drawer>
  )
}

// ---------------- 页面 ----------------
const CrsRules = () => {
  const [rel, setRel] = useState(null)
  const [nodes, setNodes] = useState([])
  const [upstream, setUpstream] = useState(null)
  const [domains, setDomains] = useState([])
  const [loading, setLoading] = useState(false)
  const [buildOpen, setBuildOpen] = useState(false)
  const [canaryRelease, setCanaryRelease] = useState(null)
  const [exclDomain, setExclDomain] = useState(null)
  const [catDomainId, setCatDomainId] = useState(null)
  const [catalog, setCatalog] = useState(null)
  const [search, setSearch] = useState([])
  const [searching, setSearching] = useState(false)
  const searchTimer = useRef(null)

  const loadReleases = useCallback(async () => {
    try {
      setRel(await crsService.releases())
    } catch (e) {
      message.error(`加载失败: ${errMsg(e)}`)
    }
  }, [])
  const loadNodes = useCallback(async () => {
    try {
      setNodes((await crsService.nodes())?.nodes || [])
    } catch (e) {
      message.error(errMsg(e))
    }
  }, [])
  useEffect(() => {
    crsService.categories().then(setCatalog).catch(() => {})
  }, [])

  const loadDomains = useCallback(async () => {
    try {
      setDomains((await crsService.domains())?.domains || [])
    } catch (e) {
      message.error(errMsg(e))
    }
  }, [])
  const loadUpstream = useCallback(async (refresh = false) => {
    try {
      setUpstream(await crsService.upstream(refresh))
    } catch (e) {
      // 上游不可达不影响页面
    }
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    await Promise.all([loadReleases(), loadNodes(), loadDomains(), loadUpstream()])
    setLoading(false)
  }, [loadReleases, loadNodes, loadDomains, loadUpstream])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const activeBuild = useMemo(
    () => (rel?.builds || []).find((b) => b.status === 'queued' || b.status === 'running') || null,
    [rel],
  )
  const inSync = nodes.filter((n) => n.match === 'match').length
  const tracked = nodes.filter((n) => n.match !== 'none').length

  const publish = async (r) => {
    try {
      await crsService.publish(r.version)
      message.success(`${r.version} 已设为最新`)
      loadReleases()
      loadNodes()
    } catch (e) {
      message.error(errMsg(e))
    }
  }

  const rollback = async () => {
    try {
      const r = await crsService.rollback()
      message.success(`已回滚到 ${r.version}`)
      loadReleases()
      loadNodes()
    } catch (e) {
      message.error(errMsg(e))
    }
  }

  const unpin = async (ids) => {
    try {
      await crsService.unpin(ids)
      loadReleases()
      loadNodes()
    } catch (e) {
      message.error(errMsg(e))
    }
  }

  const saveDomain = async (d, patch) => {
    try {
      const res = await crsService.updateDomain(d.id, patch)
      const row = res?.domain
      setDomains((prev) => {
        if (row && !row.crs_enabled) return prev.filter((x) => x.id !== d.id)
        if (!row) return prev
        const exists = prev.some((x) => x.id === row.id)
        return exists ? prev.map((x) => (x.id === row.id ? row : x)) : [...prev, row].sort((a, b) => a.domain.localeCompare(b.domain))
      })
      return true
    } catch (e) {
      message.error(errMsg(e))
      return false
    }
  }

  const onSearch = (q) => {
    clearTimeout(searchTimer.current)
    if (!q || q.length < 2) {
      setSearch([])
      return
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true)
      try {
        setSearch((await crsService.domains(q))?.domains || [])
      } catch (e) {
        setSearch([])
      } finally {
        setSearching(false)
      }
    }, 300)
  }

  const releaseColumns = [
    {
      title: '版本',
      dataIndex: 'version',
      render: (v, r) => (
        <Space size={4}>
          <Text strong={r.is_latest}>{v}</Text>
          {r.is_latest && <Tag color="green">最新</Tag>}
          {r.pinned_count > 0 && <Tooltip title="金丝雀节点"><Tag icon={<PushpinOutlined />}>{r.pinned_count}</Tag></Tooltip>}
        </Space>
      ),
    },
    {
      title: 'CRS',
      dataIndex: 'crs_tag',
      width: 110,
      render: (v, r) => (r.crs_commit ? <Tooltip title={r.crs_commit}>{v}</Tooltip> : v || '—'),
    },
    {
      title: 'SHA256',
      dataIndex: 'sha256',
      width: 130,
      render: (v) => (v ? <Text code copyable={{ text: v }} style={{ fontSize: 12 }}>{v.slice(0, 12)}</Text> : '—'),
    },
    { title: '大小', dataIndex: 'size', width: 90, render: formatSize },
    {
      title: '闸门',
      dataIndex: 'gate_summary',
      ellipsis: { showTitle: false },
      render: (v) => (v ? <Tooltip title={v}><Text style={{ fontSize: 12 }}>{v}</Text></Tooltip> : <Text type="secondary">—</Text>),
    },
    { title: '节点', dataIndex: 'node_count', width: 60 },
    {
      title: '',
      width: 160,
      render: (_, r) => (
        <Space size={0}>
          <Button size="small" type="link" onClick={() => setCanaryRelease(r)}>金丝雀</Button>
          {!r.is_latest && (
            <Popconfirm title={`将 ${r.version} 设为最新?全部未钉节点会切换`} onConfirm={() => publish(r)}>
              <Button size="small" type="link">设为最新</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const nodeColumns = [
    {
      title: '节点',
      dataIndex: 'name',
      render: (v, r) => (
        <span>
          {v} <Text type="secondary" style={{ fontSize: 12 }}>{r.ip}</Text>
          {r.status !== 'active' && <Tag style={{ marginLeft: 4 }}>{r.status}</Tag>}
        </span>
      ),
    },
    {
      title: '应装',
      dataIndex: 'expected_version',
      width: 170,
      render: (v, r) => (
        <Space size={4}>
          <span>{v || '—'}</span>
          {r.pinned_version && <Tooltip title="金丝雀钉"><PushpinOutlined style={{ color: '#1677ff' }} /></Tooltip>}
        </Space>
      ),
    },
    { title: '运行中', dataIndex: 'active_version', width: 140, render: (v) => v || '—' },
    {
      title: '状态',
      dataIndex: 'match',
      width: 90,
      filters: Object.entries(MATCH).filter(([k]) => k !== 'none').map(([k, m]) => ({ text: m.label, value: k })),
      onFilter: (v, r) => r.match === v,
      render: (v) => <Tag color={MATCH[v]?.color}>{MATCH[v]?.label || v}</Tag>,
    },
    { title: '规则数', dataIndex: 'rule_count', width: 80, render: (v) => v ?? '—' },
    {
      title: '最近错误',
      dataIndex: 'last_error',
      ellipsis: { showTitle: false },
      render: (v, r) =>
        v ? (
          <Tooltip title={`${v}${r.failures ? ` (失败 ${r.failures} 次)` : ''}`}>
            <Text type="danger" style={{ fontSize: 12 }}>{v}</Text>
          </Tooltip>
        ) : null,
    },
    {
      title: '上报',
      dataIndex: 'status_age',
      width: 100,
      render: (v, r) => (
        <Tooltip title={r.status_at}>
          <Text type={v > 600 ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>{formatAge(v)}</Text>
        </Tooltip>
      ),
    },
    {
      title: '',
      width: 70,
      render: (_, r) =>
        r.pinned_version ? (
          <Popconfirm title="解除金丝雀钉,跟随最新?" onConfirm={() => unpin([r.id])}>
            <Button size="small" type="link">解除</Button>
          </Popconfirm>
        ) : null,
    },
  ]

  const domainColumns = [
    {
      title: '域名',
      dataIndex: 'domain',
      render: (v, r) => (
        <Space size={4}>
          <span>{v}</span>
          {!r.waf_enabled && <Tooltip title="WAF 未开启,CRS 不生效"><Tag color="orange">WAF 关</Tag></Tooltip>}
        </Space>
      ),
    },
    {
      title: '模式',
      dataIndex: 'crs_mode',
      width: 150,
      render: (v, r) => (
        <Space size={4}>
          <Switch
            checked={v === 'block'}
            checkedChildren="拦截"
            unCheckedChildren="仅记录"
            onChange={(on) => saveDomain(r, { crs_mode: on ? 'block' : 'log' })}
          />
          {r.mode === 'simulate' && <Tooltip title="整站 WAF 处于演练模式,CRS 也只记录"><Tag>演练</Tag></Tooltip>}
        </Space>
      ),
    },
    {
      title: 'PL',
      dataIndex: 'paranoia_level',
      width: 90,
      render: (v, r) => (
        <Select
          size="small"
          value={v}
          style={{ width: 64 }}
          options={[1, 2, 3, 4].map((x) => ({ value: x, label: x }))}
          onChange={(x) => saveDomain(r, { paranoia_level: x })}
        />
      ),
    },
    {
      title: '阈值',
      dataIndex: 'score_threshold',
      width: 100,
      render: (v, r) => (
        <InputNumber
          size="small"
          min={1}
          max={100}
          defaultValue={v}
          key={`${r.id}-${v}`}
          style={{ width: 72 }}
          onBlur={(e) => {
            const n = parseInt(e.target.value, 10)
            if (n && n !== v) saveDomain(r, { score_threshold: n })
          }}
          onPressEnter={(e) => e.target.blur()}
        />
      ),
    },
    {
      title: '排除规则',
      dataIndex: 'exclusions_count',
      width: 100,
      render: (v, r) => (
        <Button size="small" type="link" icon={<EditOutlined />} onClick={() => setExclDomain(r)}>{v}</Button>
      ),
    },
    {
      title: '分类',
      dataIndex: 'categories',
      width: 90,
      render: (v, r) => {
        const all = catalog?.categories?.length || Object.keys(v || {}).length
        const on = Object.values(v || {}).filter(Boolean).length
        return <Button size="small" type="link" icon={<EditOutlined />} onClick={() => setCatDomainId(r.id)}>{on}/{all}</Button>
      },
    },
    {
      title: '',
      width: 50,
      render: (_, r) => (
        <Popconfirm title={`关闭 ${r.domain} 的 CRS?`} onConfirm={() => saveDomain(r, { crs_enabled: false })}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ]

  return (
    <>
      <Card
        title="CRS 规则"
        extra={
          <Space>
            {rel?.can_rollback && (
              <Popconfirm title="回滚到上一个发布的版本?" onConfirm={rollback}>
                <Button icon={<RollbackOutlined />}>回滚</Button>
              </Popconfirm>
            )}
            <Button
              type="primary"
              icon={activeBuild ? <LoadingOutlined /> : <BuildOutlined />}
              onClick={() => setBuildOpen(true)}
            >
              {activeBuild ? '构建中' : '构建新版本'}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => { loadAll(); loadUpstream(true) }} />
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}><Statistic title="最新版本" value={rel?.latest?.version || '—'} /></Col>
          <Col xs={12} md={6}><Statistic title="金丝雀" value={rel?.pins_count ?? 0} suffix="台" /></Col>
          <Col xs={12} md={6}><Statistic title="节点同步" value={inSync} suffix={`/ ${tracked}`} /></Col>
          <Col xs={12} md={6}>
            <Statistic
              title="上游"
              valueRender={() => (
                <Space size={6}>
                  <span>{upstream?.newest || '—'}</span>
                  {upstream?.has_newer && <Badge count="新版本" style={{ backgroundColor: '#fa8c16' }} />}
                </Space>
              )}
            />
          </Col>
        </Row>
        {(rel?.builds || [])[0]?.status === 'failed' && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={`最近构建 ${rel.builds[0].version} 失败`}
            action={<Button size="small" onClick={() => setBuildOpen(true)}>查看</Button>}
            closable
          />
        )}
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          columns={releaseColumns}
          dataSource={rel?.releases || []}
          pagination={false}
        />
      </Card>

      <Card size="small" title="节点" style={{ marginTop: 16 }}>
        <Table size="small" rowKey="id" columns={nodeColumns} dataSource={nodes} pagination={{ pageSize: 20, hideOnSinglePage: true }} />
      </Card>

      <Card
        size="small"
        title="启用 CRS 的域名"
        style={{ marginTop: 16 }}
        extra={
          <Select
            showSearch
            value={null}
            placeholder={<span><PlusOutlined /> 添加域名</span>}
            style={{ width: 260 }}
            filterOption={false}
            onSearch={onSearch}
            loading={searching}
            notFoundContent={null}
            options={search.filter((s) => !s.crs_enabled).map((s) => ({ value: s.id, label: s.domain }))}
            onChange={async (id) => {
              const d = search.find((s) => s.id === id)
              if (d && (await saveDomain(d, { crs_enabled: true }))) {
                message.success(`${d.domain} 已启用 CRS`)
                setSearch([])
              }
            }}
          />
        }
      >
        <Table size="small" rowKey="id" columns={domainColumns} dataSource={domains} pagination={{ pageSize: 20, hideOnSinglePage: true }} />
      </Card>

      <BuildModal
        open={buildOpen}
        onClose={() => {
          setBuildOpen(false)
          loadReleases()
        }}
        upstream={upstream}
        activeBuild={activeBuild || ((rel?.builds || [])[0]?.status === 'failed' ? rel.builds[0] : null)}
        onDone={loadReleases}
      />
      <CanaryModal
        release={canaryRelease}
        nodes={nodes}
        onClose={() => setCanaryRelease(null)}
        onDone={() => {
          setCanaryRelease(null)
          loadReleases()
          loadNodes()
        }}
      />
      <ExclusionsDrawer domain={exclDomain} onClose={() => setExclDomain(null)} onSave={saveDomain} />
      <CategoriesDrawer
        domain={domains.find((x) => x.id === catDomainId) || null}
        catalog={catalog}
        onClose={() => setCatDomainId(null)}
        onSave={saveDomain}
      />
    </>
  )
}

export default CrsRules
